import { useState, useEffect, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { apiFetch, ApiError, friendlyMessage } from '../../api/apiClient';
import { LoadingState, InlineAlert, useToast } from '../../shared/components/status';
import { GREEN, GREEN_HOVER, CHARCOAL, BORDER, MUTED, MUTED_FAINT, labelMono, bodyFont } from '../../theme/tokens2';

// ─── Shared: section wrapper ──────────────────────────────────────────────────
function Section({ icon: Icon, label, children }) {
  return (
    <Box component="section" sx={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, mb: 2.5 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 3, py: 1.75,
        borderBottom: `1px solid ${BORDER}`,
      }}>
        <Box aria-hidden sx={{
          width: 28, height: 28, flexShrink: 0,
          border: `1px solid rgba(33,131,51,0.25)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon sx={{ fontSize: '0.875rem', color: GREEN }} />
        </Box>
        <Typography component="h2" sx={{ ...labelMono, color: MUTED, fontSize: '9px' }}>{label}</Typography>
      </Box>
      {children}
    </Box>
  );
}

// ─── Shared: read-only field ──────────────────────────────────────────────────
function ReadOnlyField({ label, value }) {
  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
        <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT }}>{label}</Typography>
        <Box sx={{
          ...labelMono, fontSize: '7.5px', color: MUTED_FAINT,
          border: `1px solid ${BORDER}`, px: '5px', py: '1px',
        }}>
          READ ONLY
        </Box>
      </Box>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: CHARCOAL, ...bodyFont }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

// ─── Editable start date field ────────────────────────────────────────────────
function EditableDateField({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value || ''); }, [value]);

  useEffect(() => {
    if (editing) setTimeout(() => inputRef.current?.focus(), 0);
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onSave(draft);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter')  commit();
    if (e.key === 'Escape') { setEditing(false); setDraft(value || ''); }
  };

  // "2026-09" → "Sep 2026"
  const formatDate = (raw) => {
    if (!raw) return '—';
    const [year, month] = raw.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(month, 10) - 1]} ${year}`;
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Typography component="label" htmlFor="account-startdate" sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT, mb: 0.75, display: 'block' }}>
        RENOVATION START DATE
      </Typography>
      {editing ? (
        <Box
          component="input"
          id="account-startdate"
          ref={inputRef}
          type="month"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={handleKeyDown}
          sx={{
            border: `1px solid ${GREEN}`,
            backgroundColor: '#fff',
            px: 1.5, py: '8px',
            fontSize: '0.875rem', color: CHARCOAL,
            ...bodyFont,
            outline: 'none',
          }}
        />
      ) : (
        <Box
          component="button"
          type="button"
          onClick={() => setEditing(true)}
          aria-label={`Renovation start date: ${formatDate(value)}. Click to edit.`}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1,
            cursor: 'pointer', background: 'none', border: 'none', p: 0,
            '&:hover .edit-hint': { opacity: 1 },
            '&:focus-visible': { outline: `2px solid ${GREEN}`, outlineOffset: '2px' },
          }}
        >
          <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 500, color: GREEN, ...bodyFont }}>
            {formatDate(value)}
          </Typography>
          <Box className="edit-hint" aria-hidden sx={{ opacity: 0, transition: 'opacity 0.15s ease', display: 'flex' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </Box>
          <Typography component="span" sx={{ ...labelMono, fontSize: '8px', color: MUTED_FAINT, ml: 0.5 }}>
            Click to edit
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Password field with show/hide ────────────────────────────────────────────
function PasswordField({ id, label, value, onChange, placeholder, autoComplete }) {
  const [focused, setFocused] = useState(false);
  const [show,    setShow]    = useState(false);

  return (
    <Box>
      <Typography component="label" htmlFor={id} sx={{ ...labelMono, fontSize: '9px', color: MUTED, mb: 0.75, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ position: 'relative' }}>
        <Box
          component="input"
          id={id}
          type={show ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? '••••••••'}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          sx={{
            width: '100%', boxSizing: 'border-box',
            border: `1px solid ${focused ? GREEN : BORDER}`,
            backgroundColor: '#fff',
            px: 2, py: '11px', pr: '60px',
            fontSize: '0.875rem', color: CHARCOAL,
            ...bodyFont,
            outline: 'none',
            transition: 'border-color 0.2s ease',
            '&::placeholder': { color: 'rgba(0,0,0,0.18)' },
          }}
        />
        <Box
          component="button"
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          aria-pressed={show}
          sx={{
            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', px: '6px', py: '4px',
          }}
        >
          <Typography component="span" sx={{ ...labelMono, fontSize: '8px', color: show ? GREEN : MUTED_FAINT }}>
            {show ? 'HIDE' : 'SHOW'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── AccountPage ──────────────────────────────────────────────────────────────
export default function AccountPage() {
  const toast = useToast();

  const [profile,       setProfile]       = useState(null);
  const [profileStatus, setProfileStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  // Password form
  const [current,   setCurrent]   = useState('');
  const [next,      setNext]      = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [pwStatus,  setPwStatus]  = useState(null); // 'success' | 'error'
  const [pwMsg,     setPwMsg]     = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    setProfileStatus('loading');
    try {
      setProfile(await apiFetch('/api/householdprofile/my'));
      setProfileStatus('ready');
    } catch (err) {
      setProfileStatus('error');
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // Save a new start date. Shown straight away; put back if saving fails.
  const handleDateSave = async (newDate) => {
    const previous = profile?.startdate;
    setProfile((prev) => ({ ...prev, startdate: newDate }));
    try {
      await apiFetch('/api/householdprofile/startdate', { method: 'PATCH', body: { startdate: newDate } });
    } catch (err) {
      setProfile((prev) => ({ ...prev, startdate: previous }));
      toast.error("We couldn't save your start date. Please try again.");
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwStatus(null);
    if (!current || !next || !confirm) {
      setPwStatus('error'); setPwMsg('Please fill in all three fields.'); return;
    }
    if (next !== confirm) {
      setPwStatus('error'); setPwMsg("The new passwords don't match."); return;
    }
    if (next.length < 8) {
      setPwStatus('error'); setPwMsg('Your new password must be at least 8 characters.'); return;
    }
    if (next === current) {
      setPwStatus('error'); setPwMsg('Your new password must be different from your current one.'); return;
    }
    setPwLoading(true);
    try {
      // A wrong current password comes back as 400 (not 401), so it won't sign you out.
      await apiFetch('/api/change-password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      });
      setPwStatus('success');
      setPwMsg('Password updated. You may need to sign in again on other devices.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      setPwStatus('error');
      setPwMsg(err instanceof ApiError && err.status === 400
        ? 'Your current password is incorrect.'
        : friendlyMessage(err));
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', backgroundColor: '#f7f7f5' }}>
      <Box sx={{ maxWidth: 640, mx: 'auto', px: { xs: 3, md: 0 }, py: { xs: 5, md: 7 } }}>

        {/* Page header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Typography sx={{ ...labelMono, color: GREEN }}>Account</Typography>
            <Box sx={{ height: '1px', width: 24, backgroundColor: 'rgba(33,131,51,0.30)' }} />
          </Box>
          <Typography component="h1" sx={{
            fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
            fontWeight: 300, lineHeight: 1.1, letterSpacing: '-0.025em',
            color: CHARCOAL, ...bodyFont,
          }}>
            Your project,{' '}
            <Box component="span" sx={{ fontStyle: 'italic', color: GREEN }}>your details.</Box>
          </Typography>
        </Box>

        {/* ── Section 1: Project Details ── */}
        <Section icon={HomeOutlinedIcon} label="PROJECT DETAILS">
          {profileStatus === 'loading' && (
            <Box sx={{ p: 3 }}><LoadingState label="Loading your project details…" size="inline" /></Box>
          )}

          {profileStatus === 'error' && (
            <Box sx={{ p: 3 }}>
              <InlineAlert severity="error">
                We couldn't load your project details.{' '}
                <Box
                  component="button"
                  type="button"
                  onClick={fetchProfile}
                  sx={{ background: 'none', border: 'none', p: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                >
                  Try again
                </Box>
              </InlineAlert>
            </Box>
          )}

          {profileStatus === 'ready' && (
            <>
              <Box sx={{
                display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                borderBottom: `1px solid ${BORDER}`,
              }}>
                <Box sx={{ borderRight: { sm: `1px solid ${BORDER}` } }}>
                  <ReadOnlyField label="PROPERTY TYPE" value={profile?.property_type} />
                </Box>
                <Box>
                  <ReadOnlyField label="PROPERTY STATUS" value={profile?.property_status} />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <Box sx={{ borderRight: { sm: `1px solid ${BORDER}` } }}>
                  <EditableDateField value={profile?.startdate} onSave={handleDateSave} />
                </Box>
                <Box sx={{ px: 3, py: 2 }}>
                  <Typography sx={{ ...labelMono, fontSize: '9px', color: MUTED_FAINT, mb: 0.75 }}>
                    FLOOR PLAN
                  </Typography>
                  {profile?.floorplan_url ? (
                    <Box
                      component="a"
                      href={profile.floorplan_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                        color: GREEN, textDecoration: 'none',
                        fontSize: '0.875rem', fontWeight: 500,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      View uploaded file
                      <OpenInNewIcon sx={{ fontSize: '0.75rem' }} aria-hidden />
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.875rem', color: MUTED_FAINT, ...bodyFont }}>
                      No file uploaded
                    </Typography>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Section>

        {/* ── Section 2: Security ── */}
        <Section icon={LockOutlinedIcon} label="SECURITY">
          <Box component="form" onSubmit={handlePasswordSubmit} noValidate>
            <Box sx={{ px: 3, pt: 2.5, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <PasswordField id="pw-current" label="CURRENT PASSWORD" value={current} onChange={setCurrent} autoComplete="current-password" />

              <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: BORDER }} />
                <Typography sx={{ ...labelMono, color: MUTED_FAINT, fontSize: '8px' }}>NEW</Typography>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: BORDER }} />
              </Box>

              <PasswordField id="pw-new"     label="NEW PASSWORD"     value={next}    onChange={setNext}    placeholder="Min. 8 characters" autoComplete="new-password" />
              <PasswordField id="pw-confirm" label="CONFIRM PASSWORD" value={confirm} onChange={setConfirm} autoComplete="new-password" />

              {pwStatus && (
                <InlineAlert severity={pwStatus === 'success' ? 'success' : 'error'}>{pwMsg}</InlineAlert>
              )}
            </Box>

            <Box sx={{
              px: 3, py: 2,
              borderTop: `1px solid ${BORDER}`,
              display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
            }}>
              <Box
                component="button"
                type="submit"
                disabled={pwLoading}
                sx={{
                  backgroundColor: pwLoading ? 'rgba(33,131,51,0.50)' : GREEN,
                  color: '#fff', border: 'none',
                  cursor: pwLoading ? 'not-allowed' : 'pointer',
                  px: '24px', py: '11px',
                  fontSize: '0.875rem', fontWeight: 500, letterSpacing: '0.05em',
                  ...bodyFont,
                  transition: 'background-color 0.2s ease',
                  '&:hover:not(:disabled)': { backgroundColor: GREEN_HOVER },
                }}
              >
                {pwLoading ? 'Updating…' : 'Update Password'}
              </Box>
              <Typography sx={{ fontSize: '0.75rem', color: MUTED_FAINT, lineHeight: 1.5, ...bodyFont }}>
                You may be signed out on other devices.
              </Typography>
            </Box>
          </Box>
        </Section>

      </Box>
    </Box>
  );
}
